/**
 * SAB-793 — the one place that answers "what day is it" and "what does this bare date say".
 *
 * WHY THIS EXISTS
 * The same two conversions were reimplemented ~25 times across this codebase in four
 * different idioms. That produced SAB-412 (dates rendering a day early), SAB-788 (every
 * draft time wrong by the commissioner's offset), SAB-792 (quiet hours off by an hour all
 * season) and SAB-795 (the age gate failing open by up to 7 hours).
 *
 * THE TRAP, stated once so nobody has to rediscover it
 * A bare `date` column arrives as 'YYYY-MM-DD'. `new Date('2026-08-25')` parses that as
 * UTC MIDNIGHT — not local midnight. Render it in any zone west of UTC and it shows as
 * Aug 24. That is the entire SAB-412 class, and it is invisible from a UTC machine
 * because the error term is exactly the runtime's offset from UTC.
 *
 * So: never hand a bare date to `new Date()`. Use the functions here.
 *
 * ET is the product's day boundary everywhere else — scoring weeks, lineup locks, engine
 * quiet hours, the age gate. It is the boundary here too, stated rather than inherited.
 *
 * The edge-function twin is supabase/functions/_shared/etDate.ts. The two are separate
 * files because Deno and Vite cannot share a module here; keep them in step.
 */

export const APP_TIME_ZONE = 'America/New_York';

/** A bare calendar date with no time and no zone, as stored in a Postgres `date` column. */
export type BareDate = string; // 'YYYY-MM-DD'

const BARE = /^(\d{4})-(\d{2})-(\d{2})$/;

function assertBare(d: string): RegExpExecArray {
  const m = BARE.exec((d ?? '').trim());
  if (!m) throw new Error(`Expected a bare YYYY-MM-DD date, got ${JSON.stringify(d)}`);
  return m;
}

/**
 * Today's date in ET as 'YYYY-MM-DD'.
 *
 * `en-CA` yields ISO order directly. Deliberately NOT the round-trip
 *   new Date(new Date().toLocaleString('en-US', { timeZone: ... }))
 * which builds a Date whose INSTANT is wrong by the offset. Reading that Date's local
 * field accessors happens to be safe; calling `.toISOString()` on it is not, and both
 * forms were live in this repo.
 */
export function todayET(now: Date = new Date()): BareDate {
  return now.toLocaleDateString('en-CA', { timeZone: APP_TIME_ZONE });
}

/** The ET calendar date of an absolute instant. */
export function etDateOf(instant: Date | string): BareDate {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(d.getTime())) throw new Error(`etDateOf: invalid instant ${String(instant)}`);
  return d.toLocaleDateString('en-CA', { timeZone: APP_TIME_ZONE });
}

/**
 * The current hour in ET, 0-23.
 *
 * `hourCycle: 'h23'` and NOT `hour12: false` — the latter is permitted to render midnight
 * as 24, which silently falls outside an hour-window check at exactly the hour it should
 * match. That distinction is why SAB-792's fix uses this form.
 */
export function etHour(now: Date = new Date()): number {
  return Number(new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE, hour: '2-digit', hourCycle: 'h23',
  }).format(now));
}

/**
 * A bare date as a Date object anchored at LOCAL noon.
 *
 * Noon, not midnight: it leaves ~12 hours of slack in both directions, so the calendar
 * date survives rendering in any zone from UTC-12 to UTC+11. This is the safe form of the
 * `d + 'T12:00:00'` idiom that appears 15 times in this repo — kept because date pickers
 * and date-fns need a real Date, not because parsing is desirable.
 *
 * If you only need to DISPLAY the date, use formatBareDate instead and skip the Date.
 */
export function bareDateToDate(d: BareDate): Date {
  assertBare(d);
  return new Date(`${d}T12:00:00`);
}

/** Inverse: the calendar date a Date object represents, in the VIEWER's zone. */
export function dateToBare(d: Date): BareDate {
  if (Number.isNaN(d.getTime())) throw new Error('dateToBare: invalid Date');
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Shift a bare date by whole days, as pure string arithmetic.
 *
 * Uses a UTC-anchored Date internally precisely BECAUSE it never renders — UTC arithmetic
 * has no DST discontinuity, so adding 1 day across a spring-forward boundary cannot land
 * on the same calendar day. Doing this with a local-anchored Date is a real bug, just a
 * rarer one than the parsing trap.
 */
export function addDaysToBareDate(d: BareDate, days: number): BareDate {
  const m = assertBare(d);
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(t + days * 86400000).toISOString().slice(0, 10);
}

/**
 * Display a bare date without ever exposing a Date to the caller.
 *
 * Forces `timeZone: 'UTC'` against a UTC-anchored instant, so the rendered date is exactly
 * the stored one no matter where the viewer is. This is the form to prefer — it makes the
 * off-by-one impossible rather than merely unlikely.
 *
 *   formatBareDate('2026-08-25')                                  -> 'Aug 25, 2026'
 *   formatBareDate('2026-08-25', { month: 'short', day: 'numeric' }) -> 'Aug 25'
 */
export function formatBareDate(
  d: BareDate,
  opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
): string {
  const m = assertBare(d);
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(t).toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}

/**
 * Day of week for a bare date, 0 = Sunday.
 *
 * Computed on a UTC anchor, so it is independent of the viewer's zone. Deriving it from a
 * locally-parsed Date would inherit the same off-by-one this module exists to prevent.
 */
export function bareDateWeekday(d: BareDate): number {
  const m = assertBare(d);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
}

/**
 * The Monday of the ET week containing `d` (default: today in ET).
 *
 * Replaces a `.setDate(getDate() - ((getDay() + 6) % 7))` walk on a locally-anchored Date,
 * which is doubly fragile: it depends on the viewer's zone AND on local `setDate`
 * arithmetic behaving across a DST discontinuity.
 */
export function etWeekStartMonday(d: BareDate = todayET()): BareDate {
  return addDaysToBareDate(d, -((bareDateWeekday(d) + 6) % 7));
}

/**
 * Hours to ADD to an ET wall-clock hour to get the UTC hour, for the INSTANT given.
 * 4 during EDT, 5 during EST.
 *
 * SAB-794: this replaces `(month >= 2 && month <= 10) ? 4 : 5`, which was copied to seven
 * sites. That heuristic claims March-November is EDT. It is wrong for the tail of every
 * November -- DST 2026 ended on Nov 1, so from Nov 1 to Nov 30 it returned 4 where the
 * answer is 5, putting every overnight-pause boundary an hour out for a month. It is also
 * wrong for the first days of March in years when DST starts later than the 1st.
 *
 * Read from the IANA database via Intl rather than derived: format the instant as ET
 * wall-clock parts, reinterpret those parts AS IF they were UTC, and the gap between that
 * and the real instant IS the offset. No table, no month arithmetic, correct on both
 * transition days and in any future year.
 *
 * Takes the instant it is asked about, never `new Date()` -- a deadline in November must
 * not be measured with today's offset, which is the second half of the same bug.
 */
export function etOffsetHours(at: Date): number {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(at).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {} as Record<string, string>);
  const wallAsUTC = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour), Number(p.minute), Number(p.second),
  );
  return Math.round((at.getTime() - wallAsUTC) / 3_600_000);
}
